// src/components/CostBreakdownProgress.tsx

import { StarOutlined } from "@ant-design/icons";
import { Card, Col, Progress, Row, Statistic, Typography } from "antd";
import React from "react";
import type { CostBreakdown } from "@/types";

const { Text } = Typography;

interface CostBreakdownProgressProps {
  costBreakdown: CostBreakdown;
  showBaseCost?: boolean;
  className?: string;
}

const formatDocumentTypeName = (key: string): string => {
  // Remove "Cost" suffix and format camelCase to Title Case
  return key
    .replace(/Cost$/, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const colors: Record<string, string> = {
  base: "#4CAF50",
  multipleChoice: "#2196F3",
  shortAnswer: "#FFC107",
  ankiFlashcards: "#9C27B0",
  presentationSlides: "#FF5722",
  summary: "#009688",
  mindmap: "#3F51B5",
  readabilityAnalysis: "#FF9800",
  lessonPlan: "#795548",
  quizDiscount: "#E91E63",
};

const getCostColor = (type: string): string => {
  return colors[type.replace(/Cost$/, "")] || "#808080";
};

const CostBreakdownProgressComponent: React.FC<CostBreakdownProgressProps> = ({
  costBreakdown,
  showBaseCost = true,
  className,
}) => {
  const totalCost = costBreakdown.totalCost;

  const renderCostItem = React.useCallback(
    (key: keyof CostBreakdown, value: number) => {
      if (!key || typeof value !== "number") {
        console.debug("CostBreakdownProgress: Invalid input to renderCostItem");
        return null;
      }

      if (value === 0 || (key === "baseCost" && !showBaseCost)) return null;
      if (key === "totalCost") return null;

      if (typeof totalCost !== "number" || totalCost === 0) {
        console.debug("CostBreakdownProgress: Invalid totalCost");
        return null;
      }

      const percentage = Math.round((value / totalCost) * 100);
      const color = getCostColor(key);
      const label = formatDocumentTypeName(key);

      if (!label) {
        console.debug("CostBreakdownProgress: Invalid key name");
        return null;
      }

      return (
        <Col span={24} key={key}>
          <Text>{label}:</Text>
          <Progress
            percent={percentage}
            strokeColor={color}
            format={() => `${value} credits`}
            size="small"
            className="mb-2"
          />
        </Col>
      );
    },
    [totalCost, showBaseCost],
  );

  const costItems = React.useMemo(() => {
    return Object.entries(costBreakdown)
      .map(([key, value]) => renderCostItem(key as keyof CostBreakdown, value))
      .filter(Boolean);
  }, [costBreakdown, renderCostItem]);

  return (
    <div className={className}>
      <Row gutter={[8, 8]}>{costItems}</Row>
      <Card className="mt-4" bordered={false}>
        <Statistic
          title="Total Additional Cost:"
          value={totalCost}
          precision={0}
          valueStyle={{ color: "#3f8600" }}
          prefix={<StarOutlined />}
          suffix="credits"
        />
      </Card>
    </div>
  );
};

const CostBreakdownProgress = React.memo(CostBreakdownProgressComponent);
CostBreakdownProgress.displayName = "CostBreakdownProgress";

export default CostBreakdownProgress;
